import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Wallet } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";
import { api, errMsg, money } from "../lib/api";

const PAY = {
  paid: ["Ödendi", "bg-emerald-100 text-emerald-800 border-emerald-300"],
  partial: ["Kısmi", "bg-amber-100 text-amber-800 border-amber-300"],
  unpaid: ["Ödenmedi", "bg-rose-100 text-rose-800 border-rose-300"],
};

export const PaymentBadge = ({ status, testId }) => {
  const [l, c] = PAY[status] || PAY.unpaid;
  return <span data-testid={testId} className={`inline-flex rounded-md border px-2 py-0.5 font-mono text-[11px] uppercase tracking-wider ${c}`}>{l}</span>;
};

export const PaymentDialog = ({ order, onClose, onChanged }) => {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const remaining = order ? Math.round((order.grand_total - (order.paid_amount || 0)) * 100) / 100 : 0;
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setAmount(remaining > 0 ? String(remaining) : ""); setNote(""); }, [order?.id, remaining]); // eslint-disable-line

  const run = async (fn, msg) => {
    setBusy(true);
    try { const { data } = await fn(); toast.success(msg); onChanged(data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const add = () => {
    const a = Number(amount);
    if (!(a > 0)) return toast.error("Geçerli bir tutar girin");
    run(() => api.post(`/orders/${order.id}/payments`, { amount: a, note }), `${money(a)} tahsilat kaydedildi`);
  };

  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100%-1.5rem)] max-w-md rounded-xl bg-white" data-testid="payment-dialog">
        {order && <>
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Wallet className="h-5 w-5 text-blue-600" />Tahsilat · {order.order_no}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-2 text-center">
            {[["Tutar", order.grand_total, "pay-total"], ["Tahsil Edilen", order.paid_amount || 0, "pay-paid"], ["Kalan", remaining, "pay-remaining"]].map(([l, v, id]) => (
              <div key={id} className="rounded-lg bg-slate-50 p-2"><p className="text-[10px] uppercase text-slate-500">{l}</p><p className="font-display text-sm font-bold" data-testid={id}>{money(v)}</p></div>
            ))}
          </div>
          {(order.payments || []).length > 0 && (
            <div className="max-h-48 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200" data-testid="payment-list">
              {order.payments.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-2 p-2.5 text-sm" data-testid={`payment-item-${p.id}`}>
                  <div className="min-w-0">
                    <p className="font-semibold">{money(p.amount)}</p>
                    <p className="truncate text-xs text-slate-500">{new Date(p.created_at).toLocaleDateString("tr-TR")} {p.by_name && `· ${p.by_name}`} {p.note && `· ${p.note}`}</p>
                  </div>
                  {isAdmin && <button data-testid={`payment-delete-${p.id}`} disabled={busy} onClick={() => window.confirm("Tahsilat silinsin mi?") && run(() => api.delete(`/orders/${order.id}/payments/${p.id}`), "Tahsilat silindi")}
                    className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
                </div>
              ))}
            </div>
          )}
          {remaining > 0 && order.status !== "İptal" ? (
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input data-testid="payment-amount-input" type="number" min="0.01" step="0.01" max={remaining} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Tutar (₺)" />
                <Button data-testid="payment-full-button" type="button" variant="outline" onClick={() => setAmount(String(remaining))}>Tamamı</Button>
              </div>
              <Input data-testid="payment-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Not (nakit, havale, çek...)" />
              <Button data-testid="payment-submit-button" disabled={busy} onClick={add} className="w-full bg-slate-950 hover:bg-blue-600">Tahsilatı Kaydet</Button>
            </div>
          ) : <p className="rounded-lg bg-emerald-50 p-3 text-center text-sm font-semibold text-emerald-800" data-testid="payment-complete">Bu fatura tamamen tahsil edildi.</p>}
        </>}
      </DialogContent>
    </Dialog>
  );
};

export const CreditLimitEditor = ({ customer, credit, onSaved }) => {
  const [v, setV] = useState(String(credit.limit || ""));
  useEffect(() => setV(String(credit.limit || "")), [credit.limit]);
  const save = async () => {
    try { await api.patch(`/customers/${customer.id}/limit`, { credit_limit: Number(v) || 0 }); toast.success("Cari limit güncellendi"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3">
      <span className="text-sm font-semibold text-blue-900">Cari Limit (₺):</span>
      <Input data-testid="credit-limit-input" type="number" min="0" step="100" value={v} onChange={(e) => setV(e.target.value)} placeholder="0 = sınırsız" className="h-9 w-36 bg-white" />
      <Button data-testid="credit-limit-save-button" size="sm" onClick={save} className="bg-blue-600 hover:bg-blue-700">Kaydet</Button>
      <span className="text-xs text-blue-800/70">0 veya boş = limitsiz</span>
    </div>
  );
};

export const CustomerPaymentDialog = ({ open, customer, balance, onClose, onDone }) => {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setAmount(balance > 0 ? String(balance) : ""); setNote(""); } }, [open, balance]);
  const submit = async () => {
    const a = Number(amount);
    if (!(a > 0)) return toast.error("Geçerli bir tutar girin");
    setBusy(true);
    try {
      const { data } = await api.post(`/customers/${customer.id}/payments`, { amount: a, note });
      toast.success(`${money(a)} tahsil edildi (${data.applied.length} faturaya dağıtıldı)`);
      onDone(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100%-1.5rem)] max-w-md rounded-xl bg-white" data-testid="customer-payment-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Wallet className="h-5 w-5 text-blue-600" />Toplam Cariden Tahsilat</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Açık cari: <b data-testid="customer-payment-balance">{money(balance)}</b>. Tutar en eski faturadan başlanarak dağıtılır.</p>
        <div className="flex gap-2">
          <Input data-testid="customer-payment-amount-input" type="number" min="0.01" step="0.01" max={balance} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Tutar (₺)" />
          <Button data-testid="customer-payment-full-button" type="button" variant="outline" onClick={() => setAmount(String(balance))}>Tamamı</Button>
        </div>
        <Input data-testid="customer-payment-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Not (nakit, havale, çek...)" />
        <Button data-testid="customer-payment-submit-button" disabled={busy || !(balance > 0)} onClick={submit} className="w-full bg-slate-950 hover:bg-blue-600">Tahsilatı Kaydet</Button>
      </DialogContent>
    </Dialog>
  );
};

export const OpeningEditor = ({ url, value, onSaved, label = "Devir Bakiyesi (₺)", testId = "opening" }) => {
  const [v, setV] = useState(String(value || ""));
  useEffect(() => setV(String(value || "")), [value]);
  const save = async () => {
    try { await api.patch(url, { opening_balance: Number(v) || 0 }); toast.success("Devir bakiyesi kaydedildi"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); setV(String(value || "")); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
      <span className="text-sm font-semibold text-amber-900">{label}:</span>
      <Input data-testid={`${testId}-input`} type="number" step="0.01" value={v} onChange={(e) => setV(e.target.value)} placeholder="Önceki programdan kalan" className="h-9 w-40 bg-white" />
      <Button data-testid={`${testId}-save-button`} size="sm" onClick={save} className="bg-amber-600 hover:bg-amber-700">Kaydet</Button>
      <span className="text-xs text-amber-800/70">Önceki cari programından kalan bakiye</span>
    </div>
  );
};
